import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { warmCatalogo } from '../offline/catalogo';
import { catalogoActualizado, limpiarCatalogo } from '../offline/db';
import { aplicarIdiomaTenant } from '../i18n';
import { aplicarContexto } from '../instrument';

// Rehidrata el tenant desde localStorage para que la app funcione OFFLINE tras recargar.
function leerJson(clave, fallback) {
  try { return JSON.parse(localStorage.getItem(clave) || 'null') ?? fallback; } catch (_) { return fallback; }
}

// Lock anti-concurrencia: al arrancar, cargarSesion() y el listener onAuthStateChange podían disparar
// cargarTenants() a la vez (doble arranque → todas las consultas duplicadas). Si ya hay una en curso,
// las llamadas simultáneas reutilizan la misma promesa en vez de relanzar todo.
let _tenantsEnCurso = null;

export const useApp = create((set, get) => ({
  session: null,
  user: null,
  tenants: leerJson('hueco-tenants', []),           // [{tenant_id, rol, nombre, slug}]
  tenantActivo: leerJson('hueco-tenant-obj', null),  // {tenant_id, rol, nombre, slug}
  cargandoSesion: true,
  cargandoTenants: false,
  debeCambiarPassword: false,
  esSuperadmin: false,
  esSuperadminCargado: false, // el permiso se resuelve 1× por sesión (nunca en localStorage: es permiso, no preferencia)
  soporte: leerJson('hueco-soporte', null), // {tenant_id, nombre, original} cuando un superadmin entra a un tenant ajeno
  tema: localStorage.getItem('hueco-tema') || 'claro',
  pendientes: 0,
  catalogoFecha: null,
  suscripcion: null,

  setPendientes: (n) => set({ pendientes: n }),

  // Estado del trial/suscripción del tenant (plan, días restantes, límite, solo_lectura).
  cargarSuscripcion: async () => {
    const tid = get().tenantActivo?.tenant_id;
    if (!tid) return;
    const { data, error } = await supabase.rpc('estado_suscripcion', { p_tenant: tid });
    if (!error && data) set({ suscripcion: data });
  },

  // Sincroniza el catálogo offline y refresca la fecha visible. forzar=true = salta el throttle (sync manual).
  refrescarCatalogo: async (forzar = false) => {
    const tid = get().tenantActivo?.tenant_id;
    if (!tid) return;
    await warmCatalogo(tid, { forzar });
    set({ catalogoFecha: await catalogoActualizado() });
  },

  setTema: (tema) => {
    localStorage.setItem('hueco-tema', tema);
    document.documentElement.setAttribute('data-theme', tema === 'camara' ? 'camara' : '');
    set({ tema });
  },

  aplicarTemaInicial: () => {
    const t = get().tema;
    document.documentElement.setAttribute('data-theme', t === 'camara' ? 'camara' : '');
  },

  cargarSesion: async () => {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    set({ session, user: session?.user || null });
    if (session?.user) { await get().revisarCambioPassword(); get().cargarSuperadmin(); await get().cargarTenants(); }
    // Muestra al arranque la fecha del catálogo ya cacheado (sin depender de la red).
    set({ cargandoSesion: false, catalogoFecha: await catalogoActualizado() });
  },

  // Bloque A: ¿la cuenta tiene contraseña temporal? (metadato raw_user_meta_data.debe_cambiar_password)
  revisarCambioPassword: async () => {
    try {
      const { data } = await supabase.auth.getUser();
      const debe = data?.user?.user_metadata?.debe_cambiar_password === true;
      set({ debeCambiarPassword: debe });
      return debe;
    } catch (_) { return false; }
  },

  cargarSuperadmin: async () => {
    // 1× por sesión: es un permiso, se resuelve una vez y se limpia solo al salir/cambiar de usuario.
    if (get().esSuperadminCargado) return;
    try {
      const { data, error } = await supabase.rpc('es_superadmin');
      set({ esSuperadmin: !error && data === true, esSuperadminCargado: true });
    } catch (_) { set({ esSuperadmin: false, esSuperadminCargado: true }); }
  },

  // Bloque B: un superadmin entra a un tenant ajeno como SOPORTE (RGPD: motivo obligatorio, queda registrado).
  entrarComoSoporte: async (tenant, motivo) => {
    // Las vistas devuelven slug, no el uuid; lo resolvemos (como superadmin, RLS lo permite).
    let tid = tenant.tenant_id;
    if (!tid && tenant.slug) {
      const { data } = await supabase.from('tenants').select('id,idioma').eq('slug', tenant.slug).maybeSingle();
      tid = data?.id; tenant.idioma = tenant.idioma || data?.idioma || '';
    }
    if (!tid) throw new Error('tenant_no_encontrado');
    const { error } = await supabase.rpc('superadmin_entrar', { p_tenant: tid, p_motivo: motivo });
    if (error) throw new Error(error.message);
    const original = get().soporte?.original || get().tenantActivo || null;
    const activo = { tenant_id: tid, rol: 'owner', nombre: tenant.nombre, slug: tenant.slug || '', idioma: tenant.idioma || '' };
    const soporte = { tenant_id: tid, nombre: tenant.nombre, original };
    localStorage.setItem('hueco-tenant', activo.tenant_id);
    localStorage.setItem('hueco-tenant-obj', JSON.stringify(activo));
    localStorage.setItem('hueco-soporte', JSON.stringify(soporte));
    set({ tenantActivo: activo, soporte });
    aplicarIdiomaTenant(activo.idioma);
    get().refrescarCatalogo(true); // cambio de empresa: trae YA el catálogo correcto (salta el throttle)
    get().cargarSuscripcion();
  },

  salirSoporte: () => {
    const original = get().soporte?.original || null;
    localStorage.removeItem('hueco-soporte');
    if (original) {
      localStorage.setItem('hueco-tenant', original.tenant_id);
      localStorage.setItem('hueco-tenant-obj', JSON.stringify(original));
    }
    set({ soporte: null, tenantActivo: original });
    if (original) { aplicarIdiomaTenant(original.idioma); get().refrescarCatalogo(true); }
  },

  cargarTenants: async () => {
    // Dedupe: si ya hay una carga en curso (p.ej. arranque + listener a la vez), reutilízala.
    if (_tenantsEnCurso) return _tenantsEnCurso;
    _tenantsEnCurso = (async () => {
    set({ cargandoTenants: true });
    try {
      const { data: ures } = await supabase.auth.getUser();
      const uid = ures?.user?.id;
      // Sin red (o sin usuario): conserva el tenant cacheado para seguir operando offline.
      if (!uid) return;
      // Aplica invitaciones pendientes ANTES de leer tenants: quien se registró invitado entra en su empresa sin hacer nada.
      try { await supabase.rpc('aplicar_invitaciones'); } catch (_) {}
      // Filtrar por el usuario ACTUAL: la RLS permite ver a los co-miembros del tenant, así que sin
      // este filtro se colaban las filas de otros usuarios (p.ej. el operario veía la fila del owner).
      const { data, error } = await supabase
        .from('usuarios_tenant')
        .select('tenant_id, rol, tenants(nombre, slug, idioma)')
        .eq('activo', true)
        .eq('user_id', uid);
      if (error || !data) return; // offline u otro fallo: conserva la caché
      // Deduplica por tenant_id conservando el MAYOR privilegio (nunca bloquea a un owner).
      const RANGO = { lectura: 0, operario: 1, admin: 2, owner: 3 };
      const porTenant = {};
      data.forEach((r) => {
        const t = { tenant_id: r.tenant_id, rol: r.rol, nombre: r.tenants?.nombre || r.tenant_id, slug: r.tenants?.slug || '', idioma: r.tenants?.idioma || '' };
        const cur = porTenant[t.tenant_id];
        if (!cur || (RANGO[t.rol] ?? 0) > (RANGO[cur.rol] ?? 0)) porTenant[t.tenant_id] = t;
      });
      const tenants = Object.values(porTenant);
      const guardado = localStorage.getItem('hueco-tenant');
      const activo = tenants.find((t) => t.tenant_id === guardado) || tenants[0] || null;
      set({ tenants, tenantActivo: activo });
      localStorage.setItem('hueco-tenants', JSON.stringify(tenants));
      if (activo) {
        localStorage.setItem('hueco-tenant', activo.tenant_id);
        localStorage.setItem('hueco-tenant-obj', JSON.stringify(activo));
        aplicarIdiomaTenant(activo.idioma); // idioma del tenant salvo preferencia explícita del usuario
        aplicarContexto();
        get().refrescarCatalogo();
        get().cargarSuscripcion();
      }
    } finally {
      set({ cargandoTenants: false });
    }
    })();
    try { return await _tenantsEnCurso; } finally { _tenantsEnCurso = null; }
  },

  elegirTenant: (tenant_id) => {
    const t = get().tenants.find((x) => x.tenant_id === tenant_id);
    if (t) {
      localStorage.setItem('hueco-tenant', tenant_id);
      localStorage.setItem('hueco-tenant-obj', JSON.stringify(t));
      set({ tenantActivo: t });
      aplicarIdiomaTenant(t.idioma);
      aplicarContexto();
      get().refrescarCatalogo(true); // cambio de empresa: trae YA el catálogo correcto (salta el throttle)
    }
  },

  salir: async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('hueco-tenant');
    localStorage.removeItem('hueco-tenant-obj');
    localStorage.removeItem('hueco-tenants');
    localStorage.removeItem('hueco-soporte');
    localStorage.removeItem('hueco-catalogo-tid');
    try { await limpiarCatalogo(); } catch (_) {} // no dejar el catálogo de una empresa para el siguiente login
    set({ session: null, user: null, tenants: [], tenantActivo: null, debeCambiarPassword: false, esSuperadmin: false, esSuperadminCargado: false, soporte: null });
  },
}));
